/**
 * This device's settings (decision 16: editor comfort, themes, fonts, focus mode, keybindings; decision 66: the
 * auto-conversion switch; part 11 log, table G; part 10a: the paragraph look; part 11b: which guide the help last showed;
 * part 19b: each book's goals and today's words). They live in the device record (part 11's `device` store) as text, and
 * are read here at the boundary: the reader never throws, and a field it can't read takes its default on its own, so
 * one bad value never costs the writer the rest.
 */
import { DEFAULT_PROFILE, DEVICE_PROFILES } from '@cockatrice/layout';
import type { ProfileId } from '@cockatrice/layout';
import { deviceOn, knownDevices } from '../preview/devices.ts';
import { isId } from '@cockatrice/storage';
import { bindingProblem, COMMANDS, DEFAULT_KEYS } from './keys.ts';
import { isGuideVersion } from '../help/version.ts';
import { PARAGRAPH_LOOKS } from './paragraph-look.ts';
import type { ParagraphLookChoice } from './paragraph-look.ts';
import type { KeyBindings, Platform } from './keys.ts';

/** The editor's colours: the system's light or dark, or one chosen (G2). */
export const EDITOR_THEMES = ['system', 'light', 'dark', 'sepia'] as const;
export type EditorTheme = (typeof EDITOR_THEMES)[number];

/** The editor's typeface (G2): the system's own; the book's fonts are part 12's and the preview's. */
export const EDITOR_FONTS = ['serif', 'sans', 'mono'] as const;
export type EditorFont = (typeof EDITOR_FONTS)[number];

/** The editor's text size in CSS pixels (G3). */
export const FONT_SIZES = Object.freeze({ min: 12, max: 32, step: 1 });

/** Part 11f, WL1–WL3: what the writer shows on a wide screen: the text beside its pages, the pages alone, or the text alone. */
export const WRITER_LAYOUTS = ['side', 'pages', 'text'] as const;
export type WriterLayout = (typeof WRITER_LAYOUTS)[number];

/** Part 11d, H6: how the home page orders My books: last written in first, by title, or by words. */
export const SHELF_SORTS = ['edited', 'title', 'words'] as const;
export type ShelfSort = (typeof SHELF_SORTS)[number];

/** The preview's device profiles, in the picker's order (decision 11). */
export const PREVIEW_PROFILES: readonly ProfileId[] = Object.freeze(DEVICE_PROFILES.map((profile) => profile.id));

/** How many books remember their last open chapter (G5); more than any writer keeps on one device. */
export const MAX_REMEMBERED = 500;

/** Part 19b, G1: the most a goal can be, in words. */
export const MAX_GOAL = 10_000_000;

/** Part 19b, D10: today's words stay within this far either side of 0. */
export const MAX_TODAY = 1_000_000_000;

/** Part 11d, H5: the most words a book's count can be (500k words is decision 14's large book; this is far past it). */
export const MAX_WORDS = 1_000_000_000;

/** Part 19b, tables G and D: a book's goals and today's words, on this device. */
export interface BookWriting {
  /** G1: the words the book is to reach, or null. */
  readonly bookGoal: number | null;
  /** G1: the words to write each day, or null. */
  readonly dailyGoal: number | null;
  /** D6: the local day `today` is for (`YYYY-MM-DD`), or null before any typing. */
  readonly day: string | null;
  /** D2–D5: the net words typed in the editor on `day`. */
  readonly today: number;
  /** Part 11d, H5: the book's words as last counted on this device, for the home page; null before any count. */
  readonly words: number | null;
  /** Part 11d, H5: when its text last changed under this device's editor (milliseconds), or null. */
  readonly edited: number | null;
}

/** A book with no goals and nothing typed today (G4). */
export const NO_WRITING: BookWriting = Object.freeze({ bookGoal: null, dailyGoal: null, day: null, today: 0, words: null, edited: null });

/** The settings of this device (G1). */
export interface DeviceSettings {
  readonly editorTheme: EditorTheme;
  readonly editorFont: EditorFont;
  readonly fontSize: number;
  /** Focus mode: the chapter list and toolbars hidden, only the text. */
  readonly focusMode: boolean;
  /** Whether typed shortcuts convert (decision 66; part 10, S7). */
  readonly autoConversion: boolean;
  /** G10 (part 10a): how the editor sets paragraphs apart. */
  readonly paragraphLook: ParagraphLookChoice;
  /** G8: the device profile the preview sets pages for (decision 11; part 15). */
  readonly previewProfile: ProfileId;
  /** Part 15c, DV1–DV5: the device named in the preview's menu, one sharing `previewProfile`; null with none picked. */
  readonly previewDevice: string | null;
  /** Part 15c, DV6, DV7: the devices starred in the menu, in its order. */
  readonly favouriteDevices: readonly string[];
  /** Part 15b: whether the facing profile shows one page where two fit (decision 70). */
  readonly bookOnePage: boolean;
  /** G9 (part 11f, WL1–WL3): what a wide screen shows, the text and its pages (decision 12; part 15, Y6, Y7). */
  readonly writerLayout: WriterLayout;
  /** Part 19e, MK1, MK2: whether the writer's private notes are marked in the text ("Show private notes in text"). */
  readonly noteMarks: boolean;
  /**
   * Part 11b, table V: the version of the guide the help last showed on this device (`guideVersion`), or null; the
   * help opens by itself while it isn't the guide's version now.
   */
  readonly helpSeen: string | null;
  readonly keys: KeyBindings;
  /** The book open last, opened again at start. */
  readonly lastBook: string | null;
  /** Each book's chapter open last, by book ID. */
  readonly lastChapters: Readonly<Record<string, string>>;
  /** Part 19b, G1: each book's goals and today's words, by book ID, the one written to last at the end. */
  readonly writing: Readonly<Record<string, BookWriting>>;
  /** Part 11d, H6: how the home page orders My books. */
  readonly shelfSort: ShelfSort;
}

/** The settings of a device that has none yet (G1). */
export const DEFAULT_SETTINGS: DeviceSettings = Object.freeze({
  editorTheme: 'system',
  editorFont: 'serif',
  fontSize: 20,
  focusMode: false,
  autoConversion: true,
  paragraphLook: 'book',
  previewProfile: DEFAULT_PROFILE.id,
  previewDevice: null,
  favouriteDevices: Object.freeze([]),
  bookOnePage: false,
  writerLayout: 'side',
  noteMarks: true,
  helpSeen: null,
  keys: DEFAULT_KEYS,
  lastBook: null,
  lastChapters: Object.freeze({}),
  writing: Object.freeze({}),
  shelfSort: 'edited',
});

/** The settings format this build writes (G6). */
export const SETTINGS_VERSION = 1;

/** A field of a plain object, or undefined (an own data property only, so nothing inherited is read). */
function field(value: unknown, key: string): unknown {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;
  const descriptor = Object.getOwnPropertyDescriptor(value, key);
  return descriptor !== undefined && 'value' in descriptor ? descriptor.value : undefined;
}

/** `value` if it is one of `choices`, else `fallback` (G2). */
function choice<T extends string>(value: unknown, choices: readonly T[], fallback: T): T {
  return choices.find((each) => each === value) ?? fallback;
}

/** `value` if it is a boolean, else `fallback` (G4). */
function flag(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/** G1: a goal: a whole number from 1 to {@link MAX_GOAL}, or null for anything else (G4). */
function goal(value: unknown): number | null {
  return Number.isSafeInteger(value) && Number(value) >= 1 && Number(value) <= MAX_GOAL ? Number(value) : null;
}

const DAY = /^\d{4}-\d{2}-\d{2}$/u;

/** Part 11d, H5: a whole number from 0 to `max`, or null for anything else. */
function count(value: unknown, max: number): number | null {
  return Number.isSafeInteger(value) && Number(value) >= 0 && Number(value) <= max ? Number(value) : null;
}

/** Part 19b, G4: a book's goals and today's words, each field read on its own. */
function readWriting(value: unknown): BookWriting {
  const day = field(value, 'day');
  const today = field(value, 'today');
  const known = typeof day === 'string' && DAY.test(day);
  return Object.freeze({
    bookGoal: goal(field(value, 'bookGoal')),
    dailyGoal: goal(field(value, 'dailyGoal')),
    day: known ? day : null,
    // A count with no day it is for is no count (D6).
    today: known && Number.isSafeInteger(today) && Math.abs(Number(today)) <= MAX_TODAY ? Number(today) : 0,
    words: count(field(value, 'words'), MAX_WORDS),
    edited: count(field(value, 'edited'), Number.MAX_SAFE_INTEGER),
  });
}

/**
 * Each command's key (G7): a stored key the command can have, in command order; a missing, invalid or clashing one
 * takes the command's default, or none when another command already has that. Null (no key) is kept.
 */
export function readKeys(value: unknown, platform: Platform, reserved: ReadonlySet<string>): KeyBindings {
  const keys: Record<string, string | null> = {};
  for (const command of COMMANDS) {
    const stored = field(value, command);
    const candidates = stored === null ? [null] : typeof stored === 'string' ? [stored, DEFAULT_KEYS[command]] : [DEFAULT_KEYS[command]];
    let chosen: string | null = null;
    for (const candidate of candidates) {
      if (candidate === null || bindingProblem(bindings(keys), command, candidate, platform, reserved) === null) {
        chosen = candidate;
        break;
      }
    }
    keys[command] = chosen;
  }
  return bindings(keys);
}

/** A record of every command's key as {@link KeyBindings}. */
function bindings(keys: Readonly<Record<string, string | null>>): KeyBindings {
  return Object.freeze({
    focusMode: keys['focusMode'] ?? null,
    chapterList: keys['chapterList'] ?? null,
    previousChapter: keys['previousChapter'] ?? null,
    nextChapter: keys['nextChapter'] ?? null,
    newChapter: keys['newChapter'] ?? null,
    settings: keys['settings'] ?? null,
    help: keys['help'] ?? null,
    note: keys['note'] ?? null,
    find: keys['find'] ?? null,
  });
}

/**
 * Reads stored settings (G1–G10; part 11b, V3 and V4). Never throws: text that isn't JSON, or isn't an object, reads as the defaults, and
 * each field it can't read takes its default. Fields this build doesn't know are dropped.
 */
export function parseSettings(text: string, platform: Platform, reserved: ReadonlySet<string>): DeviceSettings {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    // Not JSON: every field takes its default.
  }
  const size = field(value, 'fontSize');
  const previewProfile = choice(field(value, 'previewProfile'), PREVIEW_PROFILES, DEFAULT_SETTINGS.previewProfile);
  const lastBook = field(value, 'lastBook');
  const helpSeen = field(value, 'helpSeen');
  const chapters = field(value, 'lastChapters');
  const lastChapters: Record<string, string> = {};
  if (typeof chapters === 'object' && chapters !== null && !Array.isArray(chapters)) {
    for (const book of Object.keys(chapters)) {
      const chapter = field(chapters, book);
      if (Object.keys(lastChapters).length < MAX_REMEMBERED && isId(book) && isId(chapter)) lastChapters[book] = chapter;
    }
  }
  const books = field(value, 'writing');
  const writing: Record<string, BookWriting> = {};
  if (typeof books === 'object' && books !== null && !Array.isArray(books)) {
    for (const book of Object.keys(books)) {
      if (Object.keys(writing).length < MAX_REMEMBERED && isId(book)) writing[book] = readWriting(field(books, book));
    }
  }
  return Object.freeze({
    editorTheme: choice(field(value, 'editorTheme'), EDITOR_THEMES, DEFAULT_SETTINGS.editorTheme),
    editorFont: choice(field(value, 'editorFont'), EDITOR_FONTS, DEFAULT_SETTINGS.editorFont),
    fontSize: Number.isSafeInteger(size) && Number(size) >= FONT_SIZES.min && Number(size) <= FONT_SIZES.max ? Number(size) : DEFAULT_SETTINGS.fontSize,
    focusMode: flag(field(value, 'focusMode'), DEFAULT_SETTINGS.focusMode),
    autoConversion: flag(field(value, 'autoConversion'), DEFAULT_SETTINGS.autoConversion),
    paragraphLook: choice(field(value, 'paragraphLook'), PARAGRAPH_LOOKS, DEFAULT_SETTINGS.paragraphLook),
    previewProfile,
    previewDevice: deviceOn(field(value, 'previewDevice'), previewProfile)?.id ?? null,
    favouriteDevices: knownDevices(field(value, 'favouriteDevices')),
    bookOnePage: flag(field(value, 'bookOnePage'), DEFAULT_SETTINGS.bookOnePage),
    writerLayout: choice(field(value, 'writerLayout'), WRITER_LAYOUTS, field(value, 'preview') === false ? 'text' : DEFAULT_SETTINGS.writerLayout),
    noteMarks: flag(field(value, 'noteMarks'), DEFAULT_SETTINGS.noteMarks),
    helpSeen: isGuideVersion(helpSeen) ? helpSeen : null,
    keys: readKeys(field(value, 'keys'), platform, reserved),
    lastBook: isId(lastBook) ? lastBook : null,
    lastChapters: Object.freeze(lastChapters),
    writing: Object.freeze(writing),
    shelfSort: choice(field(value, 'shelfSort'), SHELF_SORTS, DEFAULT_SETTINGS.shelfSort),
  });
}

/** The stored text of `settings` (G6), which {@link parseSettings} reads back as the same settings. */
export function serializeSettings(settings: DeviceSettings): string {
  return JSON.stringify({ version: SETTINGS_VERSION, ...settings });
}

/**
 * `settings` with `book`'s last chapter set to `chapter` and `book` the last book (G5). The book moves to the end of
 * the remembered ones; past {@link MAX_REMEMBERED}, the one remembered longest ago is forgotten.
 */
export function rememberChapter(settings: DeviceSettings, book: string, chapter: string | null): DeviceSettings {
  const others = Object.entries(settings.lastChapters).filter(([id]) => id !== book);
  const kept = chapter === null ? others : [...others, [book, chapter] as const];
  return Object.freeze({ ...settings, lastBook: book, lastChapters: Object.freeze(Object.fromEntries(kept.slice(-MAX_REMEMBERED))) });
}

/**
 * Part 19b, G1: `settings` with `book`'s goals and today's words changed by `change`. The book moves to the end of those
 * kept; past {@link MAX_REMEMBERED}, the one written to longest ago is forgotten.
 */
export function changeWriting(settings: DeviceSettings, book: string, change: Partial<BookWriting>): DeviceSettings {
  const others = Object.entries(settings.writing).filter(([id]) => id !== book);
  const changed = Object.freeze({ ...(settings.writing[book] ?? NO_WRITING), ...change });
  return Object.freeze({ ...settings, writing: Object.freeze(Object.fromEntries([...others, [book, changed] as const].slice(-MAX_REMEMBERED))) });
}
